export interface IUseCase {
  slug: string;
  name: string;
  emoji: string;
  filter: string;
  tagline: string;
  description: string;
  widgetSlugs: string[];
  widgetLabels: string[];
  benefits: string[];
  platforms: string[];
}

export interface IScenario {
  title: string;
  description: string;
  widgets: string;
  outcome: string;
}

export interface IMetricStat {
  value: string;
  label: string;
  description: string;
}

export interface IUseCasePageData {
  slug: string;
  name: string;
  emoji: string;
  tagline: string;
  description: string;
  benefits: string[];
  platforms: string[];
  heroHighlight: string;
  heroRest: string;
  ctaHeading: string;
  scenarios: IScenario[];
  metrics: IMetricStat[];
  primaryPlatform: string;
  integrationSteps: string[];
  widgetGuideIntro: string;
}

export const USE_CASES: IUseCase[] = [
  {
    slug: 'ecommerce',
    name: 'E-Commerce & Online Stores',
    emoji: '\uD83D\uDED2',
    filter: 'ecommerce',
    tagline: 'Turn browsers into buyers with widgets purpose-built for e-commerce conversion.',
    description:
      'From countdown timers that create urgency to review aggregators that build trust, Common Ninja gives store owners everything they need to sell more, no developer required.',
    widgetSlugs: ['countdown-bar', 'all-in-one-reviews', 'before-after', 'spinning-wheel', 'catalog', 'coupon-bar'],
    widgetLabels: ['Countdown Bar', 'All-in-One Reviews', 'Before & After Slider', 'Spinning Wheel', 'Catalog', 'Coupon Bar'],
    benefits: [
      'Increase average order value with urgency-driving countdown timers',
      'Build shopper trust with aggregated reviews from Google, Yelp, and more',
      'Reduce returns with interactive Before & After product comparisons',
      'Capture leads and reward customers with Spin the Wheel promotions',
      'Works natively with Shopify, WooCommerce, and BigCommerce',
    ],
    platforms: ['Shopify', 'WooCommerce', 'BigCommerce', 'Wix', 'Squarespace'],
  },
  {
    slug: 'content-creators',
    name: 'Content Creators & Bloggers',
    emoji: '\u270D\uFE0F',
    filter: 'content',
    tagline: 'Keep readers coming back with interactive content that goes beyond plain text.',
    description:
      'Common Ninja\'s engagement widgets transform a static blog into a destination, complete with quizzes, social feeds, audio players, and galleries that reflect your unique brand.',
    widgetSlugs: ['quiz', 'feeds', 'image-gallery', 'audio-player', 'pdf-gallery', 'testimonials'],
    widgetLabels: ['Quiz Maker', 'Social Media Feeds', 'Image Gallery', 'Audio Player', 'PDF Viewer', 'Testimonials'],
    benefits: [
      'Boost time-on-page with interactive quizzes and polls',
      'Auto-pull your latest social posts to keep content fresh',
      'Showcase visual content and portfolios with gallery widgets',
      'Share podcast episodes or music directly embedded on the page',
      'Works on WordPress, Wix, Squarespace, and any custom site',
    ],
    platforms: ['WordPress', 'Wix', 'Squarespace', 'Weebly', 'Webflow'],
  },
  {
    slug: 'agencies',
    name: 'Digital Agencies & Freelancers',
    emoji: '\uD83C\uDFE2',
    filter: 'agencies',
    tagline: 'Deliver premium, feature-rich websites for your clients in a fraction of the time.',
    description:
      'With 200+ widgets that embed anywhere and require zero maintenance, Common Ninja becomes your secret weapon for faster project delivery and happier clients.',
    widgetSlugs: ['logo-showcase', 'testimonials', 'faq', 'business-hours', 'pricing-tables', 'announcements'],
    widgetLabels: ['Logo Slider', 'Testimonials', 'FAQ', 'Opening Hours', 'Pricing Tables', 'Announcements'],
    benefits: [
      'Add sophisticated functionality to client sites without writing custom code',
      '200+ widgets means you always have the right tool for any client brief',
      'Consistent embedding experience across every platform you build on',
      'One account for unlimited client projects, easy to manage at scale',
      'White-label-friendly: widgets blend seamlessly into any design',
    ],
    platforms: ['Wix', 'Webflow', 'WordPress', 'Squarespace', 'Shopify', 'Any HTML site'],
  },
  {
    slug: 'saas',
    name: 'SaaS & Tech Companies',
    emoji: '\uD83D\uDCBB',
    filter: 'saas',
    tagline: 'Convert visitors faster and reduce churn by communicating your product\'s value with precision.',
    description:
      'Common Ninja\'s conversion and communication widgets help SaaS companies create compelling pricing pages, interactive comparisons, and social proof sections.',
    widgetSlugs: ['pricing-tables', 'comparison-tables', 'charts', 'faq', 'countdown-bar', 'all-in-one-reviews'],
    widgetLabels: ['Pricing Tables', 'Comparison Tables', 'Charts & Graphs', 'FAQ', 'Countdown Bar', 'All-in-One Reviews'],
    benefits: [
      'Build polished pricing pages that drive plan selection',
      'Create interactive feature comparison tables that do the selling for you',
      'Visualize product metrics and outcomes with customizable charts',
      'Reduce support load with comprehensive, searchable FAQ widgets',
      'Embed customer testimonials and review scores directly on landing pages',
    ],
    platforms: ['Any HTML/CSS site', 'React', 'Next.js', 'Webflow', 'WordPress'],
  },
  {
    slug: 'real-estate',
    name: 'Real Estate & Local Business',
    emoji: '\uD83C\uDFE1',
    filter: 'real-estate',
    tagline: 'Help local customers find you, trust you, and contact you, all from your website.',
    description:
      'Common Ninja offers dedicated widgets for property listings, interactive location maps, business hours, and live review feeds that make a professional first impression.',
    widgetSlugs: ['real-estate-listings', 'interactive-map', 'business-hours', 'all-in-one-reviews', 'weather', 'whatsapp-button'],
    widgetLabels: ['Real Estate Listings', 'Interactive Maps', 'Opening Hours', 'All-in-One Reviews', 'Live Weather', 'WhatsApp Chat'],
    benefits: [
      'Display and filter property listings with a mobile-optimized widget',
      'Embed color-coded interactive maps to showcase locations and service areas',
      'Show live business hours and holiday schedules automatically',
      'Aggregate reviews from Google and Yelp to build local credibility',
      'Enable one-tap WhatsApp or Telegram contact from any page',
    ],
    platforms: ['Wix', 'WordPress', 'Squarespace', 'Webflow', 'Any HTML site'],
  },
  {
    slug: 'events',
    name: 'Events & Community Managers',
    emoji: '\uD83C\uDF89',
    filter: 'events',
    tagline: 'Build excitement before, during, and after your events.',
    description:
      'Common Ninja\'s event-focused widgets help you promote upcoming events, run live bracket competitions, make important announcements, and keep your community engaged year-round.',
    widgetSlugs: ['event-listings', 'countdown-bar', 'brackets', 'announcements', 'stories', 'spinning-wheel'],
    widgetLabels: ['Event Listings', 'Countdown Bar', 'Bracket Maker', 'Announcements', 'Stories', 'Spinning Wheel'],
    benefits: [
      'List and manage upcoming events with a filterable, mobile-friendly board',
      'Build anticipation with customizable countdown timers to launch or event day',
      'Run bracket tournaments and competitions with an automated bracket widget',
      'Push important updates site-wide with notification bars and story-style cards',
      'Gamify attendance with incentivized spin-to-win promotions',
    ],
    platforms: ['WordPress', 'Wix', 'Squarespace', 'Webflow', 'Any HTML site'],
  },
  {
    slug: 'developers',
    name: 'Developers & Platform Builders',
    emoji: '\uD83D\uDC69\u200D\uD83D\uDCBB',
    filter: 'developers',
    tagline: 'Build your widget or app once, and instantly publish it across 200+ platforms.',
    description:
      'Common Ninja\'s developer platform handles hosting, billing, and distribution so you can focus entirely on building.',
    widgetSlugs: ['charts', 'pricing-tables', 'faq', 'interactive-map', 'catalog', 'quiz'],
    widgetLabels: ['Charts & Graphs', 'Pricing Tables', 'FAQ', 'Interactive Maps', 'Catalog', 'Quiz Maker'],
    benefits: [
      'Write your app once and deploy to 200+ platforms with a single API',
      'Common Ninja handles hosting, updates, and platform compliance for you',
      'Built-in monetization: set pricing tiers and collect revenue through the platform',
      'Access to 500K+ Common Ninja business users as a ready-made distribution channel',
      'Dashboard analytics to track installs, active users, and revenue in real time',
    ],
    platforms: ['Shopify', 'Wix', 'Webflow', 'WordPress', 'WooCommerce', 'BigCommerce'],
  },
];

export const FILTER_TABS = [
  { key: 'all', label: 'All Use Cases' },
  { key: 'ecommerce', label: 'E-Commerce' },
  { key: 'content', label: 'Content & Blogs' },
  { key: 'agencies', label: 'Agencies' },
  { key: 'saas', label: 'SaaS & Tech' },
  { key: 'real-estate', label: 'Real Estate & Local' },
  { key: 'events', label: 'Events & Community' },
  { key: 'developers', label: 'Developers' },
];
